import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SidenavService } from '@core/services/sidenav.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Approval } from '@interfaces';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogApprovalsEquipmentsDiffComponent } from './dialog/dialog-approvals-diff/dialog-approvals-equipments.component';
import { DialogApprovalsEquipmentsComponent } from './dialog/dialog-approvals-new/dialog-approvals-equipments.component';
import { FilterApprovalsEquipmentsComponent } from './filter/filter-approvals-equipments.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { ApprovalAndEquipment } from './services/api/approvals-equipments-api.service';
import { ApprovalsEquipmentsService } from './services/approvals-equipments.service';

@Component({
  selector: 'approvals-equipments',
  templateUrl: './approvals-equipments.component.html',
  animations: [fuseAnimations]
})
export class ApprovalsEquipmentsComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(FilterApprovalsEquipmentsComponent, { static: true }) filterApprovalsEquipmentsComponent: FilterApprovalsEquipmentsComponent;
  @ViewChild('datatable', { read: ElementRef, static: true }) datatable: ElementRef<HTMLDivElement>;

  approvalsEquipmentsReload = new Subject<any>();

  filtered = false;
  isLoadingResults = true;
  resultsLength = 0;

  totalCount: Number;
  approvals: Approval[] = [];
  loading = true;
  dataSource: Array<any> = [];
  displayedColumns: string[] = [
    'number',
    'name',
    'typeName',
    'locationName',
    'status',
    'companyName',
    'createdAt',
    'edit'
  ];

  private subscriptions = new Subject<void>();

  constructor(
    public sidenavService: SidenavService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private fuseProgressBarService: FuseProgressBarService,
    private matDialog: MatDialog,
    private approvalsEquipmentsService: ApprovalsEquipmentsService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {

    this.loading = true;
    this.sort.active = 'name';
    this.sort.direction = 'asc';

      merge(
        this.sort.sortChange,
        this.paginator.page,
        this.filterApprovalsEquipmentsComponent.filterChanged.pipe(
          tap(() => {
            this.paginator.firstPage();
          })
        ),
        this.approvalsEquipmentsReload
      )
      .pipe(
        debounceTime(500),
        takeUntil(this.subscriptions),
        switchMap(() => {
          this.sort.direction = this.sort.direction || 'asc';

          const options = {
            orderBy: `${this.sort.active}_${this.sort.direction}`,
            offset: this.paginator.pageIndex * this.paginator.pageSize,
            limit: this.paginator.pageSize
          };

          this.loading = true;
          return this.approvalsEquipmentsService.loadData({ where: this.filterApprovalsEquipmentsComponent.filterChanged.value, options });
        })
      )
      .pipe(takeUntil(this.subscriptions))
      .subscribe((result) => {
        this.datatable.nativeElement.scrollTo({
          top: 0,
        });

        this.approvals = result.items;
        this.totalCount = result.totalCount;
        this.filtered = result.filtered;
        this.loading = false;
      });

  }

  reload(): void {
    this.approvalsEquipmentsReload.next();
  }

  clearFilters(): void {
    this.filtered = false;
    this.filterApprovalsEquipmentsComponent.clearFilters();
  }

  private getDialog(result: ApprovalAndEquipment): MatDialogRef<any> {
    if (result.equipment && result.approval.status === 'pending') {
      return this.matDialog.open(DialogApprovalsEquipmentsDiffComponent, {
        panelClass: ['dialog-approval-equipment-diff'],
        data: {
          approval: result.approval,
          equipment: result.equipment
        }
      });
    }

    return this.matDialog.open(DialogApprovalsEquipmentsComponent, {
      panelClass: ['dialog-approval-equipment'],
      data: result.approval
    });
  }

  async openDialog(approval: Approval): Promise<void> {
    this.fuseProgressBarService.show();

    const result = await this.approvalsEquipmentsService.getApprovalAndEquipment({
      approvalId: approval.id,
      equipmentQrCode: approval.equipment.qrCode
    });

    this.fuseProgressBarService.hide();
    return this.getDialog(result)
      .afterClosed()
      .toPromise()
      .then((reload) => {
        if (reload) {
          this.reload();
        }
      });
  }
}
